import { useState, useEffect, useContext, createContext, useRef, useCallback, useMemo, Dispatch, SetStateAction } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast as sonnerToast } from "sonner";
import * as T from "./frontendTypes";
import { RegisterInfo, RegisterResponse, LoginResponse, checkIsUserAlive, getUserInfo, loginUser, logoutUser, registerUser, impersonateUser as impersonateUserApi, endImpersonationApi } from "../services/userService";
import Cookies from "universal-cookie";

type AsyncState<T> = 
  { status: "pending" } 
  |
  { 
    status: "rejected",
    error: unknown
  } 
  |
  {
    status: "resolved",
    value: T
  }

export function useAsync<T> (
  load: () => Promise<T>,
  dependencies: unknown[],
): AsyncState<T> {

  const [state, setState] = useState<AsyncState<T>>({ status: "pending" });

  useEffect(() => {

    let canceled = false;
    
    const func = async () => {
      try { 
        const value: T = await load();
        if (!canceled) {
          setState({ status: "resolved", value });
        }
      } catch(error) {
        if (!canceled) {
          setState({ status: "rejected", error });
        }
      }
    }
    func();

    return () => {
      canceled = true;
      setState({ status: "pending" })
    }
  }, 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  dependencies)

  return state;
}

export function useRequiredParams(paramName: string): string {
  const params = useParams();
  
  const param = params[paramName];

  if (param === undefined) {
    throw new Error(`Route param ${paramName} is required but is missing`)
  }

  return param;
}

/* 
  
  useAuth: the useAuth hook allows us to checkout user's auth state, uerRole, userRegion
  as well as handling login/logout

*/

export type AuthContextType = {
  updateUserRole: (userRole: T.UserRole) => void;
  refreshUserInfo: () => Promise<void>;
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
  setIsAuthenticated: (isAuth: boolean) => void;
  userRole: T.UserRole | null;
  userRegion: T.UserRegion | null;
  userName: string | null;
  userEmail: string | null;
  isVerified: boolean | null;
  requiresPasswordReset: boolean | null;
  settings: T.Settings | null;
  setSettings: (settings: T.Settings) => void;
  memberApplicationStatus: T.MemberApplicationStatus;
  setMemberApplicationStatus: (status: T.MemberApplicationStatus) => void;
  firmProfileStatus: 'draft' | 'complete' | null;
  pendingEmail: string | null;
  /** Server-reported environment; null while unknown (treat as production) */
  isProduction: boolean | null;
  /** View Legacy lens entitlement — server-computed from the REAL user, valid during View As */
  canViewLegacy: boolean;
  login: (loginInfo: T.LoginInfo) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  register: (registerInfo: RegisterInfo) => Promise<RegisterResponse>;
  // Impersonation (non-production only)
  isImpersonating: boolean;
  impersonatorName: string | null;
  startImpersonation: (targetUserId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

// [NOTE] Currently refreashing browser loses router history
export function useAuth(): AuthContextType {
  
  const navigate = useNavigate();
  useEffect(() => {
    let ignore = false;
    // Get and set initial auth states for cases like browser refresh
    const getInitialAuthState = async () => {
      const isAuthenticated = await checkIsUserAlive();
      if (ignore) return;
      await setAuthStates(isAuthenticated);
      if (ignore) return;
      setIsAuthInitialized(true);
    }
    getInitialAuthState();
    return () => { ignore = true; }
  }, [])

  const setAuthStates = async (isAuth: boolean) => {
    setIsAuthenticated(isAuth);
    if (isAuth) {
      const { userRole, userRegion, username, email, memberApplicationStatus, settings, isVerified, requiresPasswordReset, firmProfileStatus, pendingEmail, isProduction, canViewLegacy, impersonator } = (await getUserInfo()).user;
      setUserRole(userRole);
      setUserRegion(userRegion);
      setUserName(username);
      setUserEmail(email);
      setMemberApplicationStatus(memberApplicationStatus);
      setSettings(settings);
      setIsVerified(isVerified);
      setRequiresPasswordReset(requiresPasswordReset);
      setFirmProfileStatus(firmProfileStatus ?? null);
      setPendingEmail(pendingEmail ?? null);
      // Fail-closed: an old server that omits the field reads as production
      setIsProduction(isProduction ?? true);
      // Fail-closed: missing field means no View Legacy entitlement
      setCanViewLegacy(canViewLegacy ?? false);
      // Use server-authoritative impersonator identity instead of stale sessionStorage
      if (impersonator) {
        setImpersonatorName(impersonator.email);
      }

    } else {
      setUserRole(null);
      setUserRegion(null);
      setUserName(null);
      setUserEmail(null);
      setMemberApplicationStatus(undefined);
      setSettings(null);
      setIsVerified(null);
      setRequiresPasswordReset(null);
      setFirmProfileStatus(null);
      setPendingEmail(null);
      setIsProduction(null);
      setCanViewLegacy(false);
    }
  }

  const updateUserRole = (userRole: T.UserRole) => {
    setUserRole(userRole)
  }

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [userRole, setUserRole] = useState<T.UserRole | null>(null);
  const [userRegion, setUserRegion] = useState<T.UserRegion | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState<boolean | null>(null);
  const [memberApplicationStatus, setMemberApplicationStatus] = useState<T.MemberApplicationStatus>(undefined);
  const [settings, setSettings] = useState<T.Settings | null>(null);
  const [firmProfileStatus, setFirmProfileStatus] = useState<'draft' | 'complete' | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [isProduction, setIsProduction] = useState<boolean | null>(null);
  const [canViewLegacy, setCanViewLegacy] = useState(false);

  // Impersonation state — restored from sessionStorage on page reload
  const [isImpersonating, setIsImpersonating] = useState<boolean>(() => !!sessionStorage.getItem('_impersonatorToken'));
  const [impersonatorName, setImpersonatorName] = useState<string | null>(() => sessionStorage.getItem('_impersonatorName'));

  return {
    updateUserRole,
    async refreshUserInfo() {
      // Refresh user info without logging out (for updating flags after password change, etc.)
      await setAuthStates(true);
    },
    isAuthenticated,
    isAuthInitialized,
    setIsAuthenticated,
    userRole,
    userRegion,
    userName,
    userEmail,
    isVerified,
    requiresPasswordReset,
    memberApplicationStatus,
    setMemberApplicationStatus,
    firmProfileStatus,
    pendingEmail,
    isProduction,
    canViewLegacy,
    settings,
    setSettings,
    async login(loginInfo) {
      const loginResponse = await loginUser(loginInfo);
      if (loginResponse.success) {
        setAuthStates(true);
      }
      return loginResponse;
    },
    async logout() {
      await logoutUser();
      setAuthStates(false);
      // Hard navigation to destroy all module-level state (prevents credential bleed between accounts)
      window.location.href = '/login';
    },
    async register(registerInfo) {
      const registerResponse = await registerUser(registerInfo);
      // Only set auth states if registration succeeded and doesn't require verification
      if (registerResponse.success && !registerResponse.requiresVerification) {
        setAuthStates(true);
      }
      return registerResponse;
    },
    // Impersonation
    isImpersonating,
    impersonatorName,
    async startImpersonation(targetUserId: string) {
      const cookie = new Cookies();
      // Save current admin token before switching
      const currentToken = cookie.get("authToken");
      if (currentToken) {
        sessionStorage.setItem('_impersonatorToken', currentToken);
        sessionStorage.setItem('_impersonatorName', userEmail || '');
      }
      const res = await impersonateUserApi(targetUserId);
      if (res.success) {
        cookie.set("authToken", res.token, { path: '/' });
        // Hard navigation to reset all React state cleanly
        window.location.href = '/dashboard';
      }
    },
    async exitImpersonation() {
      const cookie = new Cookies();
      try {
        const res = await endImpersonationApi();
        if (res.success) {
          cookie.set("authToken", res.token, { path: '/' });
        }
      } catch {
        // Fallback: restore saved admin token
        const savedToken = sessionStorage.getItem('_impersonatorToken');
        if (savedToken) {
          cookie.set("authToken", savedToken, { path: '/' });
        }
      }
      sessionStorage.removeItem('_impersonatorToken');
      sessionStorage.removeItem('_impersonatorName');
      // Hard navigation to reset all React state
      window.location.href = '/dashboard';
    }
  };
}

/* 
  
  useToast: the useToast hook controls displays of toast

*/
export type ToastType = "success" | "info" | "failure";

export interface ToastContextType { 
  toastState: ToastState;
  showToast: (toastOption: ToastOption) => void;
  hideToast: () => void;
}

export interface ToastInfo {
  title?: string,
  message?: string,
  autoDisappearTimeInSeconds?: number,
}

export type ToastOption = {
  toastType: ToastType
} & ToastInfo

export type ToastState = {
  isShow: boolean;
} & ToastOption;

export const ToastContext = createContext<ToastContextType | null>(null);

export const useToast = (): {
  toastState: ToastState;
  showToast: (toastOption: ToastOption) => void;
  hideToast: () => void;
} => {

  // Keep toastState for backward compat (ToastProvider reads it)
  const [toastState, setToastState] = useState<ToastState>({
    isShow: false,
    toastType: "info"
  });

  const showToast = (toastOption: ToastOption) => {
    setToastState({...toastOption, isShow: true});

    const duration = (toastOption.autoDisappearTimeInSeconds || defaultDuration(toastOption.toastType)) * 1000;
    const opts = {
      description: toastOption.message,
      duration,
    };

    switch (toastOption.toastType) {
      case "success":
        sonnerToast.success(toastOption.title || "Success", opts);
        break;
      case "failure":
        sonnerToast.error(toastOption.title || "Error", opts);
        break;
      case "info":
        sonnerToast.info(toastOption.title || "Note", opts);
        break;
    }
  };

  const hideToast = () => {
    sonnerToast.dismiss();
    setToastState(s => ({...s, isShow: false}));
    setTimeout(() => {
      setToastState({
        isShow: false,
        toastType: "info" as ToastType,
        title: undefined,
        message: undefined,
        autoDisappearTimeInSeconds: undefined
      });
    }, 300);
  };

  return {
    toastState,
    showToast,
    hideToast,
  };
};

function defaultDuration(toastType: ToastType): number {
  switch (toastType) {
    case "success": return 5;
    case "info": return 5;
    case "failure": return 8;
  }
}

/* 
  
  useConditionalRenderContext: the useToast hook controls displays of toast

*/
type ConditionalRenderState = {[fieldKey: string]: boolean};

// [TODO] documentation of useConditionalRender
export type ConditionalRenderContextType = {
  conditionalRenderState: ConditionalRenderState,
  replaceConditionalRenderState: (newState: ConditionalRenderState) => void,
  updateConditionalRenderState: (fieldKey: string, value: boolean) => void,
}

export const ConditionalRenderContext = createContext<ConditionalRenderContextType | null>(null);

export function useConditionalRenderContext(): ConditionalRenderContextType {

  const [conditionalRenderState, setConditionalRenderState] = useState<ConditionalRenderState>({
    // [TODO] giving default value reduces integrity
    byPassTypeScriptNullPain: false,
  });

  // [NOTE] might be deletable? check and remove if time
  useEffect(() => {
  }, [conditionalRenderState])

  const replaceConditionalRenderState = (newState: ConditionalRenderState) => {
    setConditionalRenderState(newState);
  }
  
  return {
    conditionalRenderState,
    replaceConditionalRenderState,
    updateConditionalRenderState(fieldKey: string, value: boolean) {
      const updatedState = {
        ...conditionalRenderState,
        [fieldKey]: value,
      }
      // [TODO] clean up this budget solution
      if ("byPassTypeScriptNullPain" in updatedState) {
        delete updatedState.byPassTypeScriptNullPain;
      }

      setConditionalRenderState(updatedState);
    },
  };
}

/*

  useLoading is a global loading hook for loading state control

*/
export interface LoadingContextType { 
  loadingState: boolean;
  showLoading: () => void;
  hideLoading: () => void;
}

export const LoadingContext = createContext<LoadingContextType | null>(null);

export const useLoading = (): {
  loadingState: boolean;
  showLoading: () => void;
  hideLoading: () => void;
} => {

  const [loadingState, setLoadingState] = useState(false);

  const showLoading = () => {
    setLoadingState(true);
  }

  const hideLoading = () => {
    setLoadingState(false);
    }

  return {
    loadingState,
    showLoading,
    hideLoading,
  };
};

// Sidebar

export interface SidebarContextType {
  isCollapsed: boolean;
  shouldHideHeader: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  setShouldHideHeader: (hidden: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = (): SidebarContextType => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [shouldHideHeader, setShouldHideHeader] = useState(false);

  return { isCollapsed, shouldHideHeader, setIsCollapsed, setShouldHideHeader };
};

export function useSidebarContext(): SidebarContextType {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebarContext must be used within SidebarContext.Provider');
  return ctx;
}

// Feature Flags

export type FeatureFlagContextType = {
  flags: Record<string, boolean>;
  registry: T.FeatureFlagRegistry;
  isLoaded: boolean;
  fullPortalEnabled: boolean;
  embeddedSigningEnabled: boolean;
  refreshFlags: () => Promise<void>;
  updateFlags: (flags: Record<string, boolean>) => Promise<T.FeatureFlagsResponse>;
};

export const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export function useFeatureFlag(key: string): boolean {
  const ctx = useContext(FeatureFlagContext);
  // While loading or outside provider, default to visible (avoid flash)
  if (!ctx || !ctx.isLoaded) return true;
  return ctx.flags[key] ?? true;
}

export function useFullPortalEnabled(): boolean {
  const ctx = useContext(FeatureFlagContext);
  // Default to true while loading (avoid flash of restricted UI)
  if (!ctx || !ctx.isLoaded) return true;
  return ctx.fullPortalEnabled;
}

export function useEmbeddedSigningEnabled(): boolean {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx || !ctx.isLoaded) return true;
  return ctx.embeddedSigningEnabled;
}

// Language

export interface LangContextType {
  langState: T.LangState;
  setLang: (lang: T.LangState) => void;
}

export const LangContext = createContext<LangContextType | null>(null);

export const useLang = (): {
  langState: T.LangState;
  setLang: (lang: T.LangState) => void;
} => {
  const [langState, setLangState] = useState("eng" as T.LangState);

  const setLang = useCallback((lang: T.LangState) => {
    setLangState(lang);
  }, []);

  // Memoized so LangContextProvider passes a referentially stable value —
  // a fresh object every render would re-render every context consumer.
  return useMemo(() => ({ langState, setLang }), [langState, setLang]);
};

/**
 * Like useState but re-syncs when the source value (typically query data) changes.
 *
 * Use this for form fields initialized from server data — prevents stale form
 * snapshots after the underlying query refetches. Solves the anti-pattern:
 *
 *   const [name, setName] = useState(query.data?.name); // ← stale after refetch
 *
 * Replace with:
 *
 *   const [name, setName] = useStateFromQuery(query.data?.name);
 *
 * Behavior:
 * - On mount: state initializes from sourceValue
 * - User edits via setName: local edits persist as long as sourceValue is unchanged
 * - sourceValue changes by reference (refetch with new data): state resets to new value
 *
 * Trade-off: if a refetch happens WHILE the user is mid-edit AND the server
 * value has changed, the user's in-progress edit gets overwritten. This is the
 * intended behavior — the alternative (silently submitting stale data) is the
 * bug we're preventing. Acceptable for single-user editing patterns; if you
 * need true concurrent-edit safety, use react-hook-form with manual reset().
 *
 * Stable references for non-primitive fallbacks:
 * - Inline `|| []` or `|| {}` creates a new reference per render and triggers
 *   an infinite re-sync loop. Use a module-level constant:
 *     const EMPTY: string[] = [];
 *     useStateFromQuery(data?.list ?? EMPTY)
 *
 * See CLAUDE.md → State Sync Discipline for the rationale.
 */
export function useStateFromQuery<T>(sourceValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(sourceValue);
  const lastSeen = useRef(sourceValue);
  useEffect(() => {
    if (sourceValue !== lastSeen.current) {
      lastSeen.current = sourceValue;
      setState(sourceValue);
    }
  }, [sourceValue]);
  return [state, setState];
}
